import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { api, imgUrl } from "@/lib/api";
import { BookingDialog } from "@/components/BookingDialog";
import { ListingDetail } from "@/components/ListingDetail";

const BRAND_LOGOS = [
  { name: "Lamborghini", src: "https://cdn.worldvectorlogo.com/logos/lamborghini.svg" },
  { name: "Ferrari", src: "https://cdn.worldvectorlogo.com/logos/ferrari.svg" },
  { name: "McLaren", src: "https://cdn.worldvectorlogo.com/logos/mclaren.svg" },
  { name: "Rolls-Royce", src: "https://cdn.worldvectorlogo.com/logos/rolls-royce.svg" },
  { name: "Maybach", src: "https://cdn.worldvectorlogo.com/logos/maybach.svg" },
];

const CATS = [
  { key: "all", label: "All" },
  { key: "coupe", label: "Coupe" },
  { key: "convertible", label: "Convertible" },
  { key: "suv", label: "SUV" },
];

const FALLBACK_GALLERY = [imgUrl("/api/files/bookedbybenj/site/car.png")];

export default function CarsPage() {
  const [listings, setListings] = useState(null);
  const [gallery, setGallery] = useState(FALLBACK_GALLERY);
  const [sub, setSub] = useState("Exotic luxury vehicles available for music videos, film, television, events, photo and brand shoots.");
  const [cat, setCat] = useState("all");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/listings", { params: { type: "car" } }).then((r) => setListings(r.data)).catch(() => setListings([]));
    api.get("/site/content").then((r) => {
      if (r.data?.cars_gallery?.length) setGallery(r.data.cars_gallery.map(imgUrl));
      if (r.data?.site_text?.cars_sub) setSub(r.data.site_text.cars_sub);
    }).catch(() => {});
  }, []);

  const filtered = (listings || []).filter((l) => cat === "all" || (l.category || "") === cat);

  return (
    <div data-testid="cars-page">
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-8">
        {/* Brand logos */}
        <div data-testid="car-brand-logos" className="flex flex-wrap items-center gap-4 mb-10">
          {BRAND_LOGOS.map((b) => (
            <span key={b.name} className="bg-white/95 rounded-lg px-4 py-3 inline-flex items-center hover:scale-105 transition-transform">
              <img src={b.src} alt={b.name} title={b.name} className="h-8 w-auto object-contain" />
            </span>
          ))}
        </div>
        <p className="text-xs tracking-[0.4em] uppercase text-brand mb-4">Fleet</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-4">
          <h1 className="font-display text-6xl sm:text-7xl text-white">Cars</h1>
          <BookingDialog type="car" testId="book-general-car-btn" triggerLabel="Request a Car" />
        </div>
        <p className="text-sm md:text-base text-zinc-400 max-w-3xl leading-relaxed">{sub}</p>
        <p className="text-lg md:text-xl text-white font-semibold tracking-[0.15em] mt-4">$300 PER HR • PROP USE ONLY</p>
      </div>

      {/* Rotating gallery */}
      <div data-testid="cars-carousel" className="py-8 border-y border-white/10 bg-[#0A0A0A] overflow-hidden">
        <Marquee speed={35} gradient={false} pauseOnHover>
          {gallery.map((img, i) => (
            <img key={i} src={img} alt="Car" className="h-64 w-auto object-cover mx-3 border border-white/10" />
          ))}
        </Marquee>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-wrap gap-2 mb-10">
          {CATS.map((c) => (
            <button key={c.key} data-testid={`car-cat-${c.key}`} onClick={() => setCat(c.key)}
              className={`text-xs tracking-widest uppercase px-4 py-2.5 border transition-colors ${
                cat === c.key ? "bg-brand border-brand text-white" : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
              }`}>
              {c.label}
            </button>
          ))}
        </div>
        {listings === null ? (
          <p className="text-zinc-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-zinc-500 text-sm">No vehicles in this category yet — request above and we'll source one.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((l) => (
              <button key={l.id} data-testid={`listing-card-${l.id}`} onClick={() => setDetail(l)}
                className="group text-left border border-white/10 hover:border-brand/60 transition-colors duration-300 overflow-hidden hover:-translate-y-1">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={imgUrl(l.images?.[0])} alt={l.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="font-display text-2xl text-white">{l.title}</h2>
                    {l.category && <span className="text-[10px] uppercase tracking-widest text-brand border border-brand/40 px-2 py-1">{l.category}</span>}
                  </div>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed line-clamp-2">{l.description}</p>
                  <span className="inline-block text-xs tracking-widest uppercase text-brand mt-4">View & Book</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <ListingDetail listing={detail} type="car" open={!!detail} onClose={() => setDetail(null)} />
    </div>
  );
}
